'use client';

import { IS_DEV } from '@/lib/constants';
import { colorVars } from '@/theme/themeVars.css';
import { CircularProgress, Link, TextField } from '@mui/material';
import { useRouter, useSearchParams } from 'next/navigation';
import React from 'react';
import { LOGIN_REDIRECT_PARAM } from '../Auth/authConstants';
import { UserErrorMessages } from '../Auth/parseUser';
import useLogin from '../Auth/useLogin';
import Box from '../ui/Box';
import Button from '../ui/Button';
import Flex from '../ui/Flex';
import Txt from '../ui/Txt';
import { ClientLoginSettings } from './parseClientLoginSettings';

const USE_TEST_USER = IS_DEV && true; // process.env.NEXT_PUBLIC_USE_TEST_USER;

const TEST_USER_EMAIL = USE_TEST_USER && process.env.NEXT_PUBLIC_TEST_USER_EMAIL;
const TEST_USER_PW = USE_TEST_USER && process.env.NEXT_PUBLIC_TEST_USER_PW;

export interface LoginFormProps {
  settings?: ClientLoginSettings | null;
  header?: React.ReactNode;
}

const LoginForm = ({ header, settings }: LoginFormProps) => {
  const router = useRouter();
  const params = useSearchParams();

  const { login, loading, error, errors } = useLogin();

  const [email, setEmail] = React.useState(TEST_USER_EMAIL || '');
  const [password, setPassword] = React.useState(TEST_USER_PW || '');

  const [isRedirecting, setIsRedirecting] = React.useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const loginResponse = await login({ email, password });

    if (!loginResponse) return;

    const requestedPage = params.get(LOGIN_REDIRECT_PARAM);

    // Redirect to the requested page or the user's home page
    const redirect = requestedPage || loginResponse?.user?.homeUri || settings?.defaultRedirect;

    setEmail('');
    setPassword('');
    setIsRedirecting(true);

    if (redirect) {
      router.push(redirect);
    } else {
      router.refresh();
    }
  };

  const showErrors = error || !!errors?.length;
  const errorMessages = errors?.length ? errors : [UserErrorMessages.UNKNOWN];
  const showLoading = loading || isRedirecting;

  return (
    <Flex justifyContent={'center'} alignItems="center" width={'100%'}>
      <Box
        margin={4}
        maxWidth="sm"
        sx={{
          borderRadius: 6,
          overflow: 'hidden',
        }}>
        <form onSubmit={handleSubmit} noValidate>
          <Flex direction="column" gap={0} sx={{ bgcolor: colorVars.white }}>
            <Flex direction="column" paddingX={3} paddingTop={3} rowGap={2}>
              {header}
              <Txt variant="h6" bold color="primary">
                {settings?.heading ??
                  'Log in to discover the ways in which we’re poised to empower you and your members.'}
              </Txt>
            </Flex>
            <Flex direction="column" paddingX={3} paddingTop={3} paddingBottom={4} gap={2}>
              <Flex direction="column" gap={1}>
                <Txt variant="tiny" bold>
                  Email Address *
                </Txt>
                <TextField
                  disabled={showLoading}
                  variant="outlined"
                  required
                  type="email"
                  aria-label="email"
                  autoComplete="email"
                  placeholder="jane.doe@acenda.com.au"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Flex>
              <Flex direction="column" gap={1}>
                <Txt variant="tiny" bold>
                  Password *
                </Txt>
                <TextField
                  disabled={showLoading}
                  variant="outlined"
                  required
                  aria-label="password"
                  type="password"
                  autoComplete="current-password"
                  placeholder="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </Flex>

              <Flex>
                <Link href="/auth/forgot-password">
                  <Txt variant="tiny">{'Forgot your password?'}</Txt>
                </Link>
              </Flex>

              {showErrors && (
                <Flex>
                  {errorMessages.map((e) => (
                    <Txt key={e} color="error" variant="tiny">
                      {e}
                    </Txt>
                  ))}
                </Flex>
              )}
            </Flex>

            <Flex
              direction="column"
              gap={2}
              sx={{
                bgcolor: colorVars.sand100,
                padding: 2.5,
              }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                endIcon={showLoading && <CircularProgress color="inherit" size={16} />}
                disabled={showLoading}
                sx={{ width: 'fit-content' }}>
                {loading ? 'Logging in...' : isRedirecting ? 'Redirecting...' : 'Login'}
              </Button>
            </Flex>
          </Flex>
        </form>
      </Box>
    </Flex>
  );
};

export default LoginForm;
